import { createContext, use, type ComponentPropsWithRef } from "react";
import {
  Root as ToggleGroupPrimitiveRoot,
  Item as ToggleGroupPrimitiveItem,
} from "@radix-ui/react-toggle-group";
import { type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { toggleVariants } from "@/components/atoms/toggle";

const ToggleGroupContext = createContext<VariantProps<typeof toggleVariants>>({
  size: "default",
  variant: "default",
});

const ToggleGroup = ({
  className,
  variant,
  size,
  children,
  ref,
  ...props
}: ComponentPropsWithRef<typeof ToggleGroupPrimitiveRoot> &
  VariantProps<typeof toggleVariants>) => (
  <ToggleGroupPrimitiveRoot
    ref={ref}
    className={cn(
      "flex w-fit items-center justify-center gap-1",
      variant === "outline" && "gap-0 rounded-md border border-input shadow-sm",
      className,
    )}
    {...props}
  >
    <ToggleGroupContext value={{ variant, size }}>
      {children}
    </ToggleGroupContext>
  </ToggleGroupPrimitiveRoot>
);
ToggleGroup.displayName = ToggleGroupPrimitiveRoot.displayName;

const ToggleGroupItem = ({
  className,
  children,
  variant,
  size,
  ref,
  ...props
}: ComponentPropsWithRef<typeof ToggleGroupPrimitiveItem> &
  VariantProps<typeof toggleVariants>) => {
  const context = use(ToggleGroupContext);

  return (
    <ToggleGroupPrimitiveItem
      ref={ref}
      className={cn(
        toggleVariants({
          variant: context.variant || variant,
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </ToggleGroupPrimitiveItem>
  );
};
ToggleGroupItem.displayName = ToggleGroupPrimitiveItem.displayName;

export { ToggleGroup, ToggleGroupItem };
